import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaTimes } from 'react-icons/fa';
import { toEditFormDates, toEditedDatesPayload } from '../../../utils/dateOnly';
import Modal, { Field } from '../../common/Modal';
import { employeesApi, organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';

const EditGradeModal = ({
  isOpen,
  onClose,
  employeeId,
  gradeEntry,
  positionLabel,
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [grades, setGrades] = useState([]);

  const [formData, setFormData] = useState({
    gradeId: '',
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    if (isOpen) {
      fetchGrades();
      if (gradeEntry) {
        setFormData({
          gradeId: gradeEntry.grade?.id || gradeEntry.gradeId || '',
          // ADR-197: pre-fill the stored calendar day, not its local-instant reading
          ...toEditFormDates(gradeEntry)
        });
      }
    }
  }, [isOpen, gradeEntry]);

  const fetchGrades = async () => {
    try {
      const gradesData = await organizationApi.getAllGrades();
      setGrades(gradesData);
    } catch (error) {
      showAlert('Failed to load grades', 'error', error);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload = {};

      if (formData.gradeId && formData.gradeId !== gradeEntry.grade?.id) {
        payload.gradeId = formData.gradeId;
      }

      Object.assign(payload, toEditedDatesPayload(formData, gradeEntry));

      await employeesApi.updateGradeHistory(employeeId, gradeEntry.id, payload);
      showAlert('Grade history updated successfully', 'success');
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error updating grade history:', error);
      showAlert(error.message || 'Failed to update grade history', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ gradeId: '', startDate: null, endDate: null });
      onClose();
    }
  };

  const title = positionLabel ? `Edit Grade for ${positionLabel}` : 'Edit Grade';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Saving...' : 'Save Changes'}
      submitDisabled={!formData.gradeId || !formData.startDate || isSubmitting}
      width="450px"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
        <Field>
          <FormControl fullWidth>
            <InputLabel>Grade</InputLabel>
            <Select
              value={formData.gradeId}
              onChange={(e) => setFormData(prev => ({ ...prev, gradeId: e.target.value }))}
              label="Grade"
            >
              {grades.map(grade => (
                <MenuItem key={grade.id} value={grade.id}>
                  {grade.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Field>

        <Field>
          <DatePicker
            label="Start Date"
            value={formData.startDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setFormData(prev => ({ ...prev, startDate: newValue }));
              } else if (newValue === null) {
                setFormData(prev => ({ ...prev, startDate: null }));
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
        </Field>

        <Field>
          <DatePicker
            label="End Date (optional)"
            value={formData.endDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setFormData(prev => ({ ...prev, endDate: newValue }));
              } else if (newValue === null) {
                setFormData(prev => ({ ...prev, endDate: null }));
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
          {formData.endDate ? (
            <Typography
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
              onClick={() => setFormData(prev => ({ ...prev, endDate: null }))}
            >
              <FaTimes size={12} /> Clear date
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5 }}>
              Leave empty if this is the current grade
            </Typography>
          )}
        </Field>
      </Box>
    </Modal>
  );
};

export default EditGradeModal;
